import { useLifetimeOffer } from '@/hooks/use-lifetime-offer';
import {
  createLifetimeCheckoutUrl,
  getLifetimeCheckoutErrorMessage,
  useResetLoadingOnBackNavigation
} from '@/services/stripe/lifetime-checkout.service';
import { useState } from 'react';
import CLButton from '../ui-controls/default-ui-controls/button';
import { LoadingSpinWhiteCustom } from '../utils/loading.component';
import LifetimeCountdownComponent from './lifetime-countdown.component';

/**
 * A slim strip across the top of the dashboard telling the user a lifetime
 * deal is claimable and how long is left. It is not dismissable - it only
 * disappears on its own when the 24 hour window closes, when the plan is
 * bought, or when the pricing tables carry no lifetime plan.
 *
 * The CTA starts Stripe Checkout directly rather than routing through the
 * billing page - sending users to a page where they must click the same offer
 * again was just an extra step. It is also shown on every page, including
 * settings, so the promotion never loses its primary action.
 */
export default function LifetimeOfferBarComponent() {
  const { isEligible, countdown, plan, orgId } = useLifetimeOffer();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useResetLoadingOnBackNavigation(setIsLoading);

  if (!isEligible) {
    return null;
  }

  async function claim() {
    if (!orgId || !plan?.id) {
      return;
    }

    setErrorMessage('');
    setIsLoading(true);

    try {
      // A full-page navigation, not a new tab, so Stripe's redirect lands back
      // in this same session. Loading stays on until the browser leaves.
      window.location.href = await createLifetimeCheckoutUrl(orgId, plan.id);
    } catch (error) {
      setErrorMessage(getLifetimeCheckoutErrorMessage(error));
      setIsLoading(false);
    }
  }

  return (
    <div className="w-full bg-gradient-to-r from-[#FBEDC2] via-[#F7DA74] to-[#F3D053] text-black">
      <div className="flex items-center justify-between gap-3 px-4 py-2 sm:flex-wrap sm:justify-center sm:gap-x-5 sm:gap-y-2 sm:px-6 sm:py-2.5">
        {/* On phones the copy collapses into two short lines beside the CTA;
            from sm up `contents` dissolves this wrapper so every piece joins
            the centered wrap row again. */}
        <div className="flex min-w-0 flex-col items-start gap-0.5 sm:contents">
          <span className="flex min-w-0 max-w-full items-center gap-2 sm:gap-2.5">
            <span
              className="text-base leading-none sm:text-2xl"
              aria-hidden="true"
            >
              🎁
            </span>
            {/* Reinforcing copy only; on phones the price and timer say it all. */}
            <span className="hidden rounded-full bg-[#F5C63E] px-3.5 py-1 text-xs font-bold sm:inline">
              Limited Time Offer
            </span>
            {/* Only the plan name may truncate - the price always shows. */}
            <span className="flex min-w-0 items-baseline gap-1 text-sm font-extrabold sm:gap-1.5 sm:text-lg">
              <span className="shrink-0">Get</span>
              <span className="truncate">{plan?.name ?? 'Lifetime Pro'}</span>
              <span className="shrink-0">for</span>
              <span className="shrink-0 rounded bg-[#FCF3CF] px-1.5">
                ${plan?.pricing_price?.amount}
              </span>
            </span>
          </span>

          <span className="hidden text-sm font-medium lg:inline">
            One-time payment{'  '}
            <span className="mx-1 opacity-60" aria-hidden="true">
              •
            </span>{' '}
            Unlock all Pro features forever
          </span>

          <span className="flex items-center gap-1.5 text-xs sm:gap-2 sm:rounded-full sm:border sm:border-[#EDD79E] sm:bg-[#FFFBEF] sm:py-1.5 sm:pl-4 sm:pr-2 sm:text-sm">
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle
                cx="8"
                cy="8"
                r="6.5"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M8 4.5V8L10.5 9.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <span className="font-medium">Ends in</span>
            {/* The chip countdown is too wide for the phone line; swap in the
                plain-text variant there and keep the chips from sm up. */}
            <span className="sm:hidden">
              <LifetimeCountdownComponent countdown={countdown} size="sm" />
            </span>
            <span className="hidden sm:inline">
              <LifetimeCountdownComponent countdown={countdown} size="md" />
            </span>
          </span>

          {errorMessage && (
            <span role="alert" className="text-xs font-medium text-red-700">
              {errorMessage}
            </span>
          )}
        </div>

        <CLButton
          className="flex shrink-0 items-center gap-1.5 rounded-full bg-black px-3.5 py-1.5 text-xs font-semibold text-white disabled:opacity-60 sm:gap-2 sm:px-5 sm:py-2 sm:text-sm"
          disabled={isLoading}
          onClick={claim}
        >
          {isLoading && (
            <LoadingSpinWhiteCustom className="w-3 h-3"></LoadingSpinWhiteCustom>
          )}
          Claim the deal
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M2 8H14M14 8L9 3M14 8L9 13"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="sr-only">
            {' '}
            — {countdown.hours} hours and {countdown.minutes} minutes left
          </span>
        </CLButton>
      </div>
    </div>
  );
}
